import { useRef, useCallback } from 'react';

/**
 * Tracks product impressions using IntersectionObserver.
 * Deduplicates by report_id + product_id + session_id (frontend + backend).
 * Fires once per product per session when the product block enters the viewport.
 */
export default function useImpressionTracker(reportId, country, region) {
  const sessionId = useRef(
    `${reportId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  ).current;
  const sentRef = useRef(new Set());
  const bufferRef = useRef([]);
  const timerRef = useRef(null);

  const flush = useCallback(() => {
    if (bufferRef.current.length === 0) return;
    const batch = [...bufferRef.current];
    bufferRef.current = [];
    fetch(`/api/track/impressions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        report_id: reportId,
        session_id: sessionId,
        products: batch,
        country: country || '',
        region: region || '',
      }),
    }).catch(() => {});
  }, [reportId, sessionId, country, region]);

  const trackImpression = useCallback((productId, category) => {
    const key = `${productId}`;
    if (sentRef.current.has(key)) return;
    sentRef.current.add(key);
    bufferRef.current.push({ product_id: productId, category: category || '' });
    // Batch: flush after 500ms of inactivity
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 500);
  }, [flush]);

  return { trackImpression, sessionId };
}
